// Mirrors NetRiskScan.Application.Common.ApiResponse<T>
export interface ApiResponse<T> {
  success: boolean;
  code?: string | null;
  message?: string | null;
  data?: T | null;
  traceId: string;
  timestamp: string;
}

// Mirrors NetRiskScan.Api.Contracts.LanguageDto
export interface LanguageDto {
  id: number;
  code: string;
  urlCode: string;
  displayName: string;
  nativeName: string;
  enabled: boolean;
  isDefault: boolean;
  isRtl: boolean;
  sortOrder: number;
  dateFormat: string | null;
  numberFormat: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.PublicIpQueryRequest
export interface PublicIpQueryRequest {
  ipAddress?: string | null;
}

// Mirrors NetRiskScan.Domain.Models.IpQueryMeta
export interface IpQueryMeta {
  ipAddress: string;
  queriedAt: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  providerLastUpdatedAt: string | null;
  // One of AddressType values below, from PrivateNetworkClassifier.Classify — computed for every
  // query, before any provider is ever considered.
  addressType: AddressType;
  // True only when addressType is "public_routable". False means risk.scoreStatus is "not_applicable"
  // and no provider was ever called for this address (spec: loopback/private/reserved addresses can
  // never have a meaningful public-IP reputation).
  isPubliclyRoutable: boolean;
}

// Mirrors NetRiskScan.Domain.Enums.IpAddressTypes.
export type AddressType =
  | "public_routable"
  | "unspecified"
  | "loopback"
  | "private"
  | "carrier_grade_nat"
  | "link_local"
  | "documentation"
  | "multicast"
  | "reserved"
  | "broadcast";

// Mirrors NetRiskScan.Api.Contracts.PublicRiskDto — deliberately NOT the domain's PublicRisk.
//
// NetRiskScan publishes exactly one score per IP, and the public contract is narrowed server-side to
// make that structurally true rather than a rule every page has to remember: Confidence,
// DimensionScores and AppliedHardRules exist on the engine's result and never cross this boundary.
// Twice now a second 0-100 integer on this shape (Purity, then Confidence) became a second ring on
// the page and read as a second verdict. See PublicRiskContracts.cs for the full reasoning.
// Mirrors NetRiskScan.Domain.Enums.ScoreEngineStatuses.
export type ScoreStatus = "success" | "partial" | "insufficient_data" | "not_applicable";

// The one place that decides whether a result has a real score to render. Use this rather than
// comparing against "success": risk-v4.8 added "partial", which is a full, non-provisional score whose
// *evidence* was incomplete — treating it as "no data" would blank out the entire result page for the
// common case of an address whose reputation feed happened to be down.
export function hasScore(status: ScoreStatus | string): boolean {
  return status === "success" || status === "partial";
}

export interface PublicRisk {
  // The one score NetRiskScan renders: 0-100, higher = cleaner. Null exactly when scoreStatus is
  // "insufficient_data" — render "—", never 0, which on this scale would claim "extremely risky"
  // rather than "no data".
  netRiskScanIndex: number | null;
  /** @deprecated Legacy eligibility-aware label. New views use indexBand + assessmentGrade. */
  netRiskScanLevel: string | null;
  // Purely numeric band for netRiskScanIndex. Unlike the legacy netRiskScanLevel, this never changes
  // because Excellent eligibility or evidence quality is limited.
  indexBand?: string | null;
  // Evidence quality, kept on a separate axis from indexBand. New views display these together.
  assessmentGrade?: string;
  // "provider" | "aggregate".
  resultScope?: string;
  // Obsolete internal risk-direction fields (higher = riskier) — present only on snapshots saved
  // before netRiskScanIndex existed. Never render these on a new page; see resolveNetRiskScanIndex in
  // utils/riskScore.ts for the one sanctioned historical-data conversion.
  riskScore?: number | null | undefined;
  riskLevel?: string | null | undefined;
  // 0-100: how much of the relevant provider data this round obtained. Descriptive, not a verdict —
  // rendered as one line of secondary text, never as a score.
  coveragePercent: number;
  // How many providers actually contributed evidence. Disabled, quota-exhausted, circuit-open and
  // failed providers are excluded from the aggregation and so from this count.
  activeProviderCount: number;
  // "success": a real score, evidenced across every dimension that gates a clean certification.
  // "partial": a real score — same number, not provisional, not reduced — whose evidence did not cover
  // one of those dimensions (usually reputation/attack history). Render the score exactly as for
  // "success"; excellentEligible below is what actually differs. See hasScore().
  // "insufficient_data": the address is publicly routable but nothing usable came back this round —
  // never render a score, never treat this as "clean".
  // "not_applicable": the address itself (loopback/private/reserved/...) is never eligible for public-IP
  // reputation scoring — see meta.addressType. No provider was ever called.
  scoreStatus: ScoreStatus;
  // risk-v4.8: false when the evidence was not broad enough to certify this address as Excellent. It is
  // NOT a risk statement — netRiskScanIndex is identical either way and may well be 95+. Without it a
  // withheld band reads as "we measured something wrong here", which is the opposite of what happened.
  excellentEligible?: boolean;
  // Ranked, curated signal-type codes (max 4, e.g. ["Tor", "AttackSource"]) most responsible for this
  // result — map each code to a localized label client-side (see utils/riskSignals.ts). Never a number,
  // never a raw rule name.
  keySignals: string[];
  // detected | not_detected | unknown | not_supported, per curated signal type. "unknown" and
  // "not_supported" must never be rendered as if they were "not_detected" — see TriStateField.
  signalStates: Record<string, string>;
  // risk-v4.10: independent supporting-provider count per curated Detected signal type — lets the UI show
  // "Confirmed by 2 independent sources" instead of a second near-duplicate card for the same finding.
  // Optional: absent on a pre-this-feature snapshot, and a signal not Detected has no entry here.
  signalProviderCounts?: Record<string, number>;
  // risk-v4.10: "none" | "historical" | "elevated" | "confirmed" | "active" | "severe_active" — how intense
  // this round's attack-family evidence reads, finer-grained than netRiskScanLevel. Diagnostic only.
  threatSeverity?: string;
  // "complete" | "partial" | "limited" — a coarse, non-numeric statement of how much provider data this
  // round obtained. Render as one plain sentence ("some data sources were unavailable"), never as a
  // percentage — see coveragePercent's own doc comment for why a numeric coverage/confidence figure
  // stays off the public result page.
  dataCoverage: string;
  // "complete" | "partial" | "insufficient" — how much of the abuse evidence a *cleanliness* claim needs
  // was actually available (risk-v4.1). Distinct from dataCoverage, which is about provider availability
  // in general: a round where every provider answered can still be "insufficient" here, because none of
  // them was asked whether the address has an attack history.
  //
  // This exists so a capped index is readable. Without it, "59" tells a visitor "we measured moderate
  // risk"; the truth in that case is "we could not establish that this address is clean, so we will not
  // say that it is". Render it as a sentence, never as a band or a percentage.
  // Optional: a share snapshot or report permalink saved before risk-v4.1 has no such field, and an old
  // permalink must keep rendering rather than crash — same reason riskScore above is optional. Absent is
  // treated as "no ceiling statement to make", never as "insufficient".
  /** @deprecated Use assessmentGrade. Kept for pre-v4.15.x snapshots. */
  assessmentQuality?: string;
  // Which risk dimensions failed the cleanliness gate ("reputation" | "threat") — the actionable half of
  // assessmentQuality. Empty when the assessment was complete.
  unqualifiedDimensions?: string[];
  // "standard" | "infrastructure_profile". risk-v4.31: infrastructure_profile is a statement about
  // IDENTITY, not about how the number was produced — netRiskScanIndex is a real computed risk verdict on
  // both paths, and on this one it is additionally capped at the recognized profile's baseline. It used to
  // mean the opposite (a fixed Profile Base Score with the risk engine bypassed), so do not read an older
  // snapshot's value as carrying today's meaning. Optional: a share snapshot saved before this field
  // existed has neither this nor infrastructureProfile/networkContext below; a client must treat their
  // absence as "ordinary standard scoring, not recognized" rather than crashing.
  scoringMode?: string;
  // One of NetworkRoles.PublicInfrastructureRoles (e.g. "public_dns_resolver") when scoringMode is
  // "infrastructure_profile". Null/absent otherwise.
  infrastructureProfile?: string | null;
  // "What kind of address is this" — see NetworkContext (types/dto.ts below). Optional/absent on a
  // pre-this-feature snapshot; treat absence exactly like { type: "normal", isRecognized: false, ... }.
  networkContext?: NetworkContext;
  // A stable code the frontend maps to a localized headline/body — see utils/riskExplanation.ts.
  summaryCode?: string;
  // Ranked, capped at 5 — the handful of facts worth a reader's attention. Empty/absent when there is
  // nothing to say.
  keyFindings?: KeyFinding[];
  // Per curated signal type (same key set as signalStates) — the raw fact plus how strongly a reader
  // should weigh it right now. signalStates itself is never altered by this.
  signalExplanations?: Record<string, SignalExplanation>;
  // risk-v4.11: what kind of network this address belongs to, aggregated across every contributing
  // provider — never a single provider's raw label reaching this DTO unmediated (the fix for "VPN
  // misjudged as Residential / 99"). Optional/absent on a pre-this-feature snapshot; treat absence like
  // { type: "unknown", confidence: 0 }, never like a confident classification.
  networkClassification?: NetworkClassification;
  // Whether the current connection is anonymized — kept as its own axis from networkClassification (a
  // Datacenter address routed through a VPN is "network: datacenter, connection: vpn", not a conflict).
  connectionClassification?: ConnectionClassification;
  // The Index ceiling this round's network/connection classification imposed, or null/absent when none
  // applied. Diagnostic only — netRiskScanIndex above already reflects it.
  indexCeiling?: number | null;
  // risk-v4.12: the anonymity_type axis — "commercial_vpn" | "shared_vpn" | "public_proxy" |
  // "residential_proxy" | "tor" | "shared_exit" | "none". Render it BESIDE networkClassification.type,
  // never merged into it: "datacenter + commercial VPN" and "datacenter + public proxy" are different
  // addresses with different consequences. Optional/absent on a pre-risk-v4.12 snapshot; treat absence
  // as "none" (no anonymizing egress was established), never as a confident finding either way.
  anonymityProfile?: string;
  // risk-v4.12: "low" | "moderate" | "high" — how much friction third-party services are likely to put
  // in front of this address. NOT a score and never rendered as one (see PublicRiskDto.ServiceFriction):
  // it answers a question netRiskScanIndex structurally cannot, namely "nothing is wrong with this
  // address, so why is every site challenging me?"
  serviceFriction?: string;
  // risk-v4.12: the structured "why is this the score" — five stable codes, mapped to localized copy by
  // utils/riskExplanation.ts. Absent on a pre-risk-v4.12 snapshot.
  explanation?: RiskExplanationDetail;
  // risk-v4.12: per Detected signal type, which CATEGORY of intelligence established it —
  // "network_intelligence" | "anonymity_intelligence" | "reputation_intelligence" |
  // "threat_intelligence". This is the public vocabulary that replaces naming a vendor; pair it with
  // signalProviderCounts for "confirmed by 2 independent threat-intelligence sources".
  signalSourceCategories?: Record<string, string>;
  // evidence-quality-v1: "high" | "medium" | "low" | "insufficient" — how much to trust
  // netRiskScanIndex given coverage, evidence strength and freshness. Categorical, like assessmentGrade
  // beside it, for the same reason coveragePercent is descriptive text rather than a ring: a second
  // plausible-looking number here is exactly the shape that made the Purity Score and raw Confidence
  // read as second scores before. Optional: absent on a pre-evidence-quality-v1 snapshot; render the
  // Evidence Quality panel only when present.
  evidenceConfidence?: string;
  // evidence-quality-v1: "high" | "medium" | "low" | "unknown" — whether the independent evidence
  // behind this round's verdict corroborates itself. NOT a risk signal: "low" means sources disagreed
  // or only one ever spoke, never which side was right.
  evidenceAgreement?: string;
  // evidence-quality-v1: seconds since the most recently observed signal behind this result was
  // collected, or null when no signal evidence was collected at all. Render as relative time
  // ("18 minutes ago"), never as a score.
  evidenceFreshnessSeconds?: number | null;
}

// Mirrors NetRiskScan.Api.Contracts.RiskExplanationDetailDto. Every value is a stable code, never prose —
// see utils/riskExplanation.ts for the code -> copy mapping and its English fallbacks.
export interface RiskExplanationDetail {
  // Same value as PublicRisk.summaryCode.
  summary: string;
  // "primary_reason:threat_evidence" | ":reputation_history" | ":historical_record" |
  // ":network_identity" | ":no_risk_found" | ":insufficient_data" — which axis decided the number.
  primaryReason: string;
  // "network:{network_type}:{anonymity_profile}".
  networkContext: string;
  // "threat:{severity}" over ThreatSeverities. "threat:none" is a real answer.
  threatContext: string;
  // "service_friction:{level}".
  serviceImpact: string;
  // "confidence:{assessment_quality}:{data_coverage}".
  confidenceNote: string;
}

// Mirrors NetRiskScan.Api.Contracts.NetworkClassificationDto.
export interface NetworkClassification {
  // One of NetworkTypes ("residential" | "mobile" | "business_access" | "hosting" | "datacenter" |
  // "public_infrastructure" | "education" | "government" | "unknown"). This is the current best
  // estimate even at low confidence; confirmed flags retain their separate stricter threshold.
  type: string;
  // 0-1.
  confidence: number;
  evidenceCount: number;
  // True when independent providers disagreed about this address's network type.
  conflict: boolean;
}

// Mirrors NetRiskScan.Api.Contracts.ConnectionClassificationDto.
export interface ConnectionClassification {
  // One of ConnectionTypes ("direct" | "vpn" | "proxy" | "residential_proxy" | "tor" | "relay" | "unknown").
  type: string;
  confidence: number;
  conflict: boolean;
}

// Mirrors NetRiskScan.Api.Contracts.NetworkContextDto.
export interface NetworkContext {
  // One of NetworkContextTypes (e.g. "public_dns", "cdn", "normal").
  type: string;
  isRecognized: boolean;
  isSharedInfrastructure: boolean;
  // "high" | "none" — categorical, not a number (same reasoning as dataCoverage/assessmentQuality: a raw
  // score-shaped integer here risks reading as a second score next to netRiskScanIndex).
  matchStrength: string;
  // This round's own Organization/ISP text, for a "operated by {organization}" line — never a per-brand
  // lookup table. Null when unavailable.
  organization?: string | null;
  hostname?: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.KeyFindingDto.
export interface KeyFinding {
  // One of "positive" | "warning" | "negative" | "identity".
  kind: string;
  // A stable code — either a curated signal type (map with utils/riskSignals.ts's signalLabel) or one of
  // RiskExplanationEngine's own finding codes (e.g. "recognized_infrastructure", "no_anonymity_detected").
  code: string;
}

// Mirrors NetRiskScan.Api.Contracts.SignalExplanationDto.
export interface SignalExplanation {
  // detected | not_detected | unknown | not_supported — identical to signalStates[type].
  rawState: string;
  // active_risk | significant_risk | historical | contextualized | weak_evidence | network_identity |
  // not_detected | unknown | not_supported.
  displayState: string;
  // positive | neutral | caution | elevated | severe.
  severity: string;
}


// Mirrors NetRiskScan.Domain.Models.IpRiskFlags
export interface IpRiskFlags {
  isProxy: boolean | null;
  isVpn: boolean | null;
  isTor: boolean | null;
  isRelay: boolean | null;
  isHosting: boolean | null;
  isDatacenter: boolean | null;
  isResidential: boolean | null;
  isMobile: boolean | null;
  isBot: boolean | null;
  isCrawler: boolean | null;
  isAbuser: boolean | null;
  isBlacklisted: boolean | null;
}

// Mirrors NetRiskScan.Domain.Models.ProviderConsensusEntry — one independently-queried,
// independently-normalized provider result for the same IP, for side-by-side display only. Never
// influences the report's own risk/purity/flags, which always come from the single priority-first
// provider the main query pipeline already produced.
export interface ProviderConsensusEntryDto {
  providerCode: string;
  success: boolean;
  flags: IpRiskFlags | null;
  // Flat on the wire (mirrors PublicConsensusEntryDto's own flat NetRiskScanIndex/NetRiskScanLevel
  // properties, not a nested PublicRisk) — this provider's own view of the same IP, scoped to the one
  // score and its level; Coverage/ActiveProviderCount/ScoreStatus are meaningless for a single
  // provider so they don't exist here.
  netRiskScanIndex: number | null;
  /** @deprecated Legacy eligibility-aware label; new views use indexBand + assessmentGrade. */
  netRiskScanLevel: string | null;
  indexBand?: string | null;
  assessmentGrade?: string | null;
  resultScope?: "provider" | "aggregate" | string;
  scoreStatus?: string | null;
  evidenceCount?: number;
  detectedSignalCount?: number;
  cleanSignalCount?: number;
  unknownSignalCount?: number;
}

// Mirrors NetRiskScan.Domain.Models.IpClassification
export interface IpClassification {
  // Canonical best-estimate axes added in v4.15.x. These stay meaningful even when confirmed flags are null.
  networkType?: string | null;
  connectionTypeV2?: string | null;
  proxyType: string | null;
  usageType: string | null;
  /** @deprecated Legacy provider network/access label. Use networkType and connectionTypeV2. */
  connectionType: string | null;
}

// Mirrors NetRiskScan.Domain.Models.IpNetworkInfo
export interface IpNetworkInfo {
  asn: string | null;
  networkRange: string | null;
  isp: string | null;
  organization: string | null;
  hostname: string | null;
}

// Mirrors NetRiskScan.Domain.Models.IpLocationInfo
export interface IpLocationInfo {
  continentCode: string | null;
  continentName: string | null;
  countryCode: string | null;
  countryName: string | null;
  regionCode: string | null;
  regionName: string | null;
  cityName: string | null;
  postalCode: string | null;
  timeZone: string | null;
  latitude: number | null;
  longitude: number | null;
}

// Mirrors NetRiskScan.Domain.Models.IpCurrencyInfo
export interface IpCurrencyInfo {
  currencyCode: string | null;
  currencyName: string | null;
  currencySymbol: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.PublicDeviceEstimatesDto — what the old public "confidence" block
// keeps. Its third field, detectionConfidence, is gone from the public API: a 0-100 number rendered as
// "检测置信度 93%" directly under the risk score is indistinguishable from a second score to a reader
// who is not parsing the field name. These two are counts of devices, so neither can be read as a
// rating of the address.
export interface PublicDeviceEstimates {
  estimatedDevicesOnIp: number | null;
  estimatedDevicesOnSubnet: number | null;
}

// Mirrors NetRiskScan.Api.Contracts.PublicTorNetworkDto — Tor Project network detail. Evidence behind
// the score, not a second score: exitPolicyLevel is a code (strict/moderate/broad/very_broad/unknown)
// the UI maps to localized copy, and exitPolicySummaryDetail is raw port text meant only for a "查看详情"
// disclosure, never the headline (spec §XVIII/§XIX).
export interface PublicTorNetwork {
  // not_tor | tor_middle | tor_guard | tor_exit
  nodeType: string;
  // tor-classification-v1: relay membership and exit capability are two different facts (spec §XIX).
  // A Middle or Guard relay is isRelay: true, isExit: false — a Tor node that carries no exit traffic,
  // and must be labelled "Tor Relay", never "Tor Exit Node".
  isRelay: boolean;
  isExit: boolean;
  // Flagged BadExit by the directory authorities. Independent of isExit — a BadExit is still an exit.
  isBadExit: boolean;
  // middle | guard | exit
  role: string;
  // strict | moderate | broad | very_broad | unknown — null unless nodeType is tor_exit.
  exitPolicyLevel: string | null;
  exitPolicySummaryDetail: string | null;
  // Named service categories this exit's policy allows, e.g. ["Web", "Mail"] — null unless
  // exitPolicyLevel is set.
  allowedServiceCategories: string[] | null;
  // fresh | stale
  dataFreshness: string;
}

// Mirrors NetRiskScan.Api.Contracts.PublicIpRiskResultDto — the public, provider-agnostic result
// shape. No Provider name, no ExtraData, no ErrorCode/ErrorMessage (spec §6/§15).
export interface PublicIpRiskResultDto {
  // Public permalink key — resolves via GET /api/public/ip/share/{shareId}.
  shareId: string;
  meta: IpQueryMeta;
  risk: PublicRisk;
  flags: IpRiskFlags;
  classification: IpClassification;
  network: IpNetworkInfo;
  location: IpLocationInfo;
  currency: IpCurrencyInfo;
  deviceEstimates: PublicDeviceEstimates;
  torNetwork?: PublicTorNetwork | null;
}

// ===== DNS leak test =====

export interface DnsLeakSessionCreatedDto {
  sessionId: string;
  hosts: string[];
  expiresAtUtc: string;
}

export interface DnsResolverDto {
  ip: string;
  asn: string | null;
  organization: string | null;
  countryCode: string | null;
  queryCount: number;
  firstSeenAtUtc: string;
  lastSeenAtUtc: string;
}

export interface DnsLeakSessionResultDto {
  status: "waiting" | "completed";
  expiresAtUtc: string;
  resolvers: DnsResolverDto[];
}

// ===== Claude access checker =====

// Mirrors NetRiskScan.Api.Contracts.ClaudeRegionSupportDto
export interface ClaudeRegionSupportDto {
  webSupported: boolean;
  apiSupported: boolean;
  dataUpdatedAt: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.ClaudeServiceStatusDto — indicator mirrors Statuspage.io's
// vocabulary: none|minor|major|critical, or "unknown" when the status page itself was unreachable.
export interface ClaudeServiceStatusDto {
  indicator: string;
  description: string;
}

// Mirrors NetRiskScan.Api.Contracts.ClaudeAccessContextDto — reuses the same nested value types as
// PublicIpRiskResultDto. Deliberately does NOT include claude.ai/api.anthropic.com reachability —
// only the visitor's own browser can observe that (see utils/claudeProbe.ts).
export interface ClaudeAccessContextDto {
  meta: IpQueryMeta;
  location: IpLocationInfo;
  network: IpNetworkInfo;
  flags: IpRiskFlags;
  risk: PublicRisk;
  region: ClaudeRegionSupportDto;
  serviceStatus: ClaudeServiceStatusDto;
}

// ===== Full Environment Check =====

// Mirrors NetRiskScan.Api.Contracts.EnvironmentCheckSubmissionRequest — every field beyond ipAddress
// is a client-observed signal already produced by an existing detector (WebRTC/dual-stack/DNS-leak
// utils), not re-probed server-side.
export interface EnvironmentCheckSubmissionRequest {
  ipAddress?: string;
  ipv6Address?: string;
  ipv6CountryCode?: string;
  // The full resolver list from the browser's own DNS-leak session (utils/dnsLeak.ts) — each entry is
  // already enriched client-side via the same /api/public/ip/query pipeline the standalone leak-test
  // page uses, so nothing here re-runs a lookup server-side.
  dnsResolvers?: EnvironmentCheckDnsResolverSubmission[];
  webRtcPublicIps?: string[];
  browserTimezone?: string;
  browserLanguage?: string;
  // Raw measurements from utils/speedTest.ts — the score is computed server-side, never trusted from
  // the client (see NetworkSpeedScoring).
  speedTest?: EnvironmentCheckSpeedTestSubmission;
  // Client-side reachability probe (utils/claudeProbe.ts) — only meaningful as "network connectivity",
  // never as proof of account/billing eligibility (see claudeProbe.ts's own comment).
  claudeReachable?: boolean;
}

export interface EnvironmentCheckDnsResolverSubmission {
  ip?: string;
  asn?: string;
  organization?: string;
  countryCode?: string;
  queryCount?: number;
}

export interface EnvironmentCheckSpeedTestSubmission {
  nodeCode?: string;
  nodeName?: string;
  latencyMs?: number;
  jitterMs?: number;
  stabilityPercent?: number;
  downloadMbps?: number;
  uploadMbps?: number;
}

// Mirrors NetRiskScan.Api.Contracts.ConsistencySignalDto
export interface ConsistencySignalDto {
  signal: string;
  value: string | null;
  status: string;
}

// Mirrors NetRiskScan.Api.Contracts.FindingDto
export interface FindingDto {
  code: string;
  severity: "critical" | "warning" | "info" | "pass";
  title: string;
  observed: string;
  meaning: string;
  recommendedAction: string | null;
  scoreImpact: number;
}

// Mirrors NetRiskScan.Api.Contracts.EnvironmentReportDto — the permanent result of a Full
// Environment Check, reused by both the check page (immediately after submission) and the /r/:id
// permalink page.
export interface EnvironmentReportDto {
  id: string;
  createdAt: string;
  score: number;
  grade: "excellent" | "good" | "fair" | "risky" | "poor";
  confidence: "high" | "medium" | "low";
  consistencyScore: number;
  inconsistencyCount: number;
  isPublic: boolean;
  meta: IpQueryMeta;
  risk: PublicRisk;
  flags: IpRiskFlags;
  network: IpNetworkInfo;
  location: IpLocationInfo;
  signals: ConsistencySignalDto[];
  findings: FindingDto[];
  // Informational only, does not affect score/grade — see NetworkScanAiAccessEntry below.
  aiAccess: NetworkScanAiAccessEntry[] | null;
  claudeReachable: boolean | null;
  // Informational only, does not affect score/grade.
  speedTest: EnvironmentSpeedTestResultDto | null;
  // The full DNS resolver list observed by the browser's own leak session — null on reports created
  // before this field existed, or when the leak session never completed.
  dnsResolvers: EnvironmentDnsResolverDto[] | null;
  // Provider Consensus — see ProviderConsensusEntryDto. Null on older reports or when fewer than two
  // providers were enabled at generation time.
  consensus: ProviderConsensusEntryDto[] | null;
  // Informational only, does not affect score/grade — see NetworkScanStreamingEntry.
  streaming: NetworkScanStreamingEntry[] | null;
  // anonymity-debug-v1: how this connection's IPv4/IPv6/WebRTC/DNS exits relate (split tunnel, IPv6 leak,
  // WebRTC/DNS mismatch). Supporting observation only — isAuthoritative is always false and it never changes
  // risk/flags/score. Optional: absent on reports created before it existed.
  connectionObservation?: EnvironmentConnectionObservationDto | null;
}

// Mirrors NetRiskScan.Domain.Models.ConnectionObservation
export interface EnvironmentConnectionObservationDto {
  assessment: "anonymized" | "partially_anonymized" | "not_anonymized" | "undetermined";
  anonymizedFamilies: string[];
  splitTunnelSuspected: boolean | null;
  ipv6LeakSuspected: boolean | null;
  dualStackAsnMismatch: boolean | null;
  dualStackCountryMismatch: boolean | null;
  webRtcPublicIpMismatch: boolean | null;
  dnsNetworkMismatch: boolean | null;
  dnsPublicResolver: boolean | null;
  findings: { code: string; strength: "supporting" | "weak" | "informational"; detail: string }[];
  isAuthoritative: boolean;
}

// Mirrors NetRiskScan.Api.Contracts.EnvironmentDnsResolverDto
export interface EnvironmentDnsResolverDto {
  ip: string;
  asn: string | null;
  organization: string | null;
  countryCode: string | null;
  queryCount: number;
}

// Mirrors NetRiskScan.Api.Contracts.EnvironmentSpeedTestResultDto
export interface EnvironmentSpeedTestResultDto {
  nodeCode: string | null;
  nodeName: string | null;
  latencyMs: number | null;
  jitterMs: number | null;
  stabilityPercent: number | null;
  downloadMbps: number | null;
  uploadMbps: number | null;
  score: number;
  grade: "excellent" | "good" | "fair" | "poor";
  scoreAlgorithmVersion: number;
}

// ===== Server Deep Scan (VPS Agent) =====

// Mirrors NetRiskScan.Api.Contracts.NetworkScanServerInfo (via Domain.Models.NetworkScanServerInfo)
export interface NetworkScanServerInfo {
  hostname: string | null;
  osDistribution: string | null;
  kernelVersion: string | null;
  architecture: string | null;
  cpuModel: string | null;
  cpuCores: number | null;
  virtualization: string | null;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanTcpInfo
export interface NetworkScanTcpInfo {
  congestionControl: string | null;
  ipv4Connective: boolean | null;
  ipv6Connective: boolean | null;
  // "open" | "moderate" | "strict"
  natType: string | null;
  natExternalIp: string | null;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanBgpInfo
export interface NetworkScanBgpInfo {
  prefix: string | null;
  rir: string | null;
  upstreams: string[] | null;
  peers: string[] | null;
  ixps: string[] | null;
}

// "success" | "partial" | "failed" | "skipped"
export type NetworkTestEntryStatus = "success" | "partial" | "failed" | "skipped";

// "DEPENDENCY_MISSING" | "DNS_ERROR" | "CONNECTION_REFUSED" | "TIMEOUT" | "SERVER_BUSY" |
// "IPV6_UNAVAILABLE" | "COMMAND_ERROR" | "PARSE_ERROR" | "LOW_DATA_MODE" | "NO_NODES" | "UNKNOWN"
export type NetworkTestErrorCode = string;

// Mirrors NetRiskScan.Domain.Models.NetworkScanGlobalEntry — one iperf3 run against one public global
// node. Throughput is bits per second throughout the stack; convert to Mbps only at render time.
export interface NetworkScanGlobalEntry {
  nodeCode: string;
  city: string;
  countryCode: string | null;
  region: string | null;
  provider: string | null;
  protocol: "ipv4" | "ipv6" | null;
  tcpLatencyMs: number | null;
  tcpLatencyMinMs: number | null;
  tcpLatencyMaxMs: number | null;
  tcpLossPercent: number | null;
  uploadBps: number | null;
  uploadRetransmits: number | null;
  downloadBps: number | null;
  downloadRetransmits: number | null;
  status: NetworkTestEntryStatus;
  errorCode: NetworkTestErrorCode | null;
  errorMessage: string | null;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanSpeedtestEntry — one Ookla run pinned to one China
// carrier's server ID.
export interface NetworkScanSpeedtestEntry {
  carrier: "ct" | "cu" | "cm" | "global";
  nodeCode: string | null;
  city: string | null;
  serverId: string | null;
  serverName: string | null;
  protocol: "ipv4" | "ipv6" | null;
  pingMs: number | null;
  jitterMs: number | null;
  uploadBps: number | null;
  downloadBps: number | null;
  downloadLatencyMs: number | null;
  uploadLatencyMs: number | null;
  packetLossPercent: number | null;
  status: NetworkTestEntryStatus;
  errorCode: NetworkTestErrorCode | null;
  errorMessage: string | null;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanAiAccessEntry
export interface NetworkScanAiAccessEntry {
  providerCode: string;
  displayName: string;
  webSupported: boolean | null;
  apiSupported: boolean | null;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanStreamingEntry — informational only, a curated region
// allowlist (not a real-time catalog/library check), same caveat as NetworkScanAiAccessEntry.
export interface NetworkScanStreamingEntry {
  providerCode: string;
  displayName: string;
  supported: boolean | null;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanRouteChange
export interface NetworkScanRouteChange {
  carrier: "ct" | "cu" | "cm";
  previousResultCode: string;
  previousDisplayName: string;
  currentResultCode: string;
  currentDisplayName: string;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanChinaLatencyEntry
export interface NetworkScanChinaLatencyEntry {
  province: string;
  carrier: "ct" | "cu" | "cm";
  avgRttMs: number | null;
  bestRttMs: number | null;
  worstRttMs: number | null;
  packetLossPercent: number | null;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanRouteHop
export interface NetworkScanRouteHop {
  hop: number;
  ip: string | null;
  asn: string | null;
  organization: string | null;
  latencyMs: number | null;
}

// Mirrors NetRiskScan.Domain.Models.NetworkScanRouteResult
export interface NetworkScanRouteResult {
  carrier: "ct" | "cu" | "cm";
  resultCode: string;
  displayName: string;
  grade: "premium" | "standard" | "poor" | "unknown";
  hops: NetworkScanRouteHop[] | null;
}

// Mirrors NetRiskScan.Api.Contracts.NetworkScanAxisScoreDto
export interface NetworkScanAxisScoreDto {
  code: string;
  score: number | null;
  // "success" | "partial" | "unsupported" | "failed" | "skipped"
  status: string;
  // Plain-language "why this number" explanation — null for a Skipped/Failed axis.
  reason: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.NetworkScanReportDto — the permanent result of a Server Deep
// Scan submitted by the open-source Agent, reachable at /r/:publicId.
export interface NetworkScanReportDto {
  publicId: string;
  createdAt: string;
  scanType: "default" | "full" | "lite" | "china" | "ip";
  agentVersion: string | null;
  isPublic: boolean;
  meta: IpQueryMeta;
  risk: PublicRisk;
  flags: IpRiskFlags;
  network: IpNetworkInfo;
  location: IpLocationInfo;
  ipv6Address: string | null;
  reverseDns: string | null;
  server: NetworkScanServerInfo | null;
  tcp: NetworkScanTcpInfo | null;
  bgp: NetworkScanBgpInfo | null;
  chinaLatency: NetworkScanChinaLatencyEntry[] | null;
  chinaRoutes: NetworkScanRouteResult[] | null;
  globalSpeedtest: NetworkScanGlobalEntry[] | null;
  chinaSpeedtest: NetworkScanSpeedtestEntry[] | null;
  lowDataMode: boolean;
  aiAccess: NetworkScanAiAccessEntry[] | null;
  streaming: NetworkScanStreamingEntry[] | null;
  // Provider Consensus — see ProviderConsensusEntryDto. Null on older reports or when fewer than two
  // providers were enabled at generation time.
  consensus: ProviderConsensusEntryDto[] | null;
  routeChanges: NetworkScanRouteChange[] | null;
  overallScore: number;
  overallGrade: "excellent" | "good" | "fair" | "risky" | "poor";
  scoreAlgorithmVersion: number;
  axes: NetworkScanAxisScoreDto[];
  summary: string;
}

// Mirrors NetRiskScan.Api.Contracts.NetworkScanReportSummaryDto — one row in the public report library.
export interface NetworkScanReportSummaryDto {
  publicId: string;
  createdAt: string;
  countryCode: string | null;
  cityName: string | null;
  asn: string | null;
  isp: string | null;
  overallScore: number | null;
  overallGrade: string | null;
  chinaTelecomRoute: string | null;
  chinaUnicomRoute: string | null;
  chinaMobileRoute: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.NetworkScanReportPageDto
export interface NetworkScanReportPageDto {
  items: NetworkScanReportSummaryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// Mirrors NetRiskScan.Api.Contracts.NetworkTestNodePublicDto
export interface NetworkTestNodePublicDto {
  code: string;
  name: string;
  nodeType: string;
  protocol: string | null;
  countryCode: string | null;
  region: string | null;
  city: string | null;
  carrier: string | null;
  provider: string | null;
  host: string;
  port: number;
  portRangeStart: number | null;
  portRangeEnd: number | null;
  ipv4Supported: boolean;
  ipv6Supported: boolean;
  priority: number;
}

// ===== Public CMS =====

export interface PublicCmsAlternateDto {
  languageCode: string;
  urlCode: string;
  path: string;
  isDefault: boolean;
}

// Mirrors NetRiskScan.Api.Contracts.PublicCmsRedirectResponse
export interface PublicCmsRedirectResponse {
  found: boolean;
  newPath: string | null;
}

export interface PublicCmsContentResponse {
  contentId: number;
  contentType: string;
  templateKey: string;
  coverImageUrl: string | null;
  featuredImageAlt: string | null;
  languageCode: string;
  title: string;
  slug: string;
  summary: string | null;
  body: string;
  seoTitle: string | null;
  seoDescription: string | null;
  seoKeywords: string | null;
  canonicalUrl: string | null;
  openGraphTitle: string | null;
  openGraphDescription: string | null;
  openGraphImageUrl: string | null;
  twitterCard: string;
  structuredDataJson: string | null;
  seoIndex: boolean;
  seoFollow: boolean;
  publishedAt: string | null;
  updatedAt: string;
  author: string;
  viewCount: number;
  category: PublicCmsCategorySummary | null;
  tags: PublicCmsTagSummary[];
  alternates: PublicCmsAlternateDto[];
}

export interface PublicCmsCategorySummary {
  id: number;
  code: string;
  name: string;
  slug: string;
}

export interface PublicCmsTagSummary extends PublicCmsCategorySummary {}

export interface PublicArticleListItem {
  contentId: number;
  title: string;
  slug: string;
  path: string;
  summary: string | null;
  coverImageUrl: string | null;
  featuredImageAlt: string | null;
  category: PublicCmsCategorySummary | null;
  publishedAt: string | null;
  updatedAt: string;
  readingTimeMinutes: number;
  isFeatured: boolean;
  sortOrder: number;
  viewCount: number;
  author: string;
}

export interface PublicArticlePage {
  items: PublicArticleListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PublicSiteConfiguration {
  adSenseReviewMode: boolean;
}

export interface PublicCmsCategoryDto {
  id: number;
  parentId: number | null;
  code: string;
  name: string;
  slug: string;
  description: string | null;
  sortOrder: number;
}

// ===== Domain & Website Security Check =====

// Mirrors NetRiskScan.Api.Contracts.DomainScanRequest
export interface DomainScanRequest {
  input?: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.DomainMxRecordDto
export interface DomainMxRecordDto {
  host: string;
  preference: number;
}

// Mirrors NetRiskScan.Api.Contracts.DomainDnsInfoDto
export interface DomainDnsInfoDto {
  aRecords: string[];
  aaaaRecords: string[];
  cnameRecord: string | null;
  mxRecords: DomainMxRecordDto[];
  nsRecords: string[];
  txtRecords: string[];
  caaRecords: string[];
  dnssecAuthenticated: boolean;
}

// Mirrors NetRiskScan.Api.Contracts.DomainRegistrationInfoDto
export interface DomainRegistrationInfoDto {
  registrar: string | null;
  registeredAt: string | null;
  expiresAt: string | null;
  lastChangedAt: string | null;
  statuses: string[];
  nameservers: string[];
  domainAgeDays: number | null;
  daysUntilExpiry: number | null;
}

// Mirrors NetRiskScan.Api.Contracts.DomainTlsInfoDto
export interface DomainTlsInfoDto {
  httpsSupported: boolean;
  protocol: string | null;
  issuer: string | null;
  subject: string | null;
  subjectAlternativeNames: string[];
  notBefore: string | null;
  notAfter: string | null;
  daysUntilExpiry: number | null;
  hostnameMatches: boolean;
  chainValid: boolean;
  certificateSha1: string | null;
  /**
   * ti-v2.4: null means the check did not run — no certificate was read, or the local abuse.ch snapshot is
   * missing or too old for a negative to mean anything. Rendering null as "not blacklisted" would report an
   * unperformed check as a passed one, so the UI has three cases here rather than two.
   */
  certificateBlacklisted: boolean | null;
  certificateBlacklistReason: string | null;
  certificateBlacklistedAt: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.DomainRedirectHopDto
export interface DomainRedirectHopDto {
  url: string;
  statusCode: number;
}

// Mirrors NetRiskScan.Api.Contracts.DomainSecurityHeadersDto
export interface DomainSecurityHeadersDto {
  contentSecurityPolicy: string | null;
  strictTransportSecurity: string | null;
  xContentTypeOptions: string | null;
  xFrameOptions: string | null;
  referrerPolicy: string | null;
  permissionsPolicy: string | null;
  crossOriginOpenerPolicy: string | null;
  crossOriginResourcePolicy: string | null;
  server: string | null;
  xPoweredBy: string | null;
}

// Mirrors NetRiskScan.Api.Contracts.DomainHttpInfoDto
export interface DomainHttpInfoDto {
  httpRedirectsToHttps: boolean;
  finalUrl: string;
  finalStatusCode: number;
  redirectChain: DomainRedirectHopDto[];
  headers: DomainSecurityHeadersDto;
}

// Mirrors NetRiskScan.Api.Contracts.DomainScanScoreDto
export interface DomainScanScoreDto {
  total: number;
  level: string;
  dnsScore: number;
  tlsScore: number;
  headersScore: number;
  registrationScore: number;
}

// Mirrors NetRiskScan.Api.Contracts.DomainScanResultDto
export interface DomainScanResultDto {
  input: string;
  host: string;
  hostIsIpLiteral: boolean;
  dns: DomainDnsInfoDto | null;
  registration: DomainRegistrationInfoDto | null;
  tls: DomainTlsInfoDto | null;
  http: DomainHttpInfoDto | null;
  score: DomainScanScoreDto;
  scannedAt: string;
}

// ===== Public Ads =====

export interface PublicAdResolveResponse {
  hasAd: boolean;
  creativeId: number | null;
  creativeType: string | null;
  title: string | null;
  imageUrl: string | null;
  targetUrl: string | null;
  htmlContent: string | null;
  scriptContent: string | null;
  adSenseClientId: string | null;
  adSenseSlotId: string | null;
  width: number | null;
  height: number | null;
  isResponsive: boolean;
  lazyLoad: boolean;
  refreshIntervalSeconds: number | null;
}
